import {
    CreditCardIcon,
    FolderIcon,
    LogoutIcon,
    UserIcon,
    ShoppingCartIcon,
    LocationMarkerIcon,
    InformationCircleIcon,
    ShoppingBagIcon,
    UploadIcon,
} from '@heroicons/react/outline'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { logout } from '../helpers/account'
import { classNames, log, _isImpersonated } from '../helpers/utils'
import { ChevronDownIcon } from '@heroicons/react/solid'
import { Menu, Transition } from '@headlessui/react'

const navigation = {
    Orders: {
        icon: ShoppingCartIcon,
        items: [
            {
                name: 'Orders',
                icon: ShoppingBagIcon,
                href: '/my-account/orders',
                count: 0,
                current: false,
            },
            {
                name: 'Subscriptions',
                icon: FolderIcon,
                href: '/my-account/subscriptions',
                count: 0,
                current: false,
            },
        ],
    },
    Uploads: {
        icon: UploadIcon,
        href: '/my-account/uploads',
        count: 0,
        current: false,
    },
    'My Account': {
        icon: UserIcon,
        items: [
            // {
            //     name: 'Dashboard',
            //     icon: HomeIcon,
            //     href: '/my-account',
            //     current: true,
            // },
            {
                name: 'Account Details',
                icon: InformationCircleIcon,
                href: '/my-account/account-details',
                current: false,
            },
            {
                name: 'Addresses',
                icon: LocationMarkerIcon,
                href: '/my-account/addressess',
                count: 0,
                current: false,
            },
            {
                name: 'Return Address Profiles',
                icon: LocationMarkerIcon,
                href: '/my-account/return-address-profiles',
                count: 0,
                current: false,
            },
            {
                name: 'Saved Cards',
                icon: CreditCardIcon,
                href: '/my-account/saved-cards',
                current: false,
            },
        ],
    },
}

function AccountNav() {
    const router = useRouter()
    const [impersonated, setImpersonated] = useState(false)

    const handleLogout = async () => {
        try {
            await logout()
        } catch (e) {
            log(e)
        }
    }

    useEffect(() => {
        setImpersonated(_isImpersonated())
    }, [])

    return (
        <nav
            className="md:flex px-2 space-y-1 md:bg-slate-50 py-2 my-4"
            aria-label="Sidebar">
            {Object.keys(navigation).map((item) => {
                if ('items' in navigation[item]) {
                    const GroupIcon = navigation[item].icon
                    return (
                        <Menu
                            as="div"
                            className="relative inline-block text-left items-center"
                            key={item}>
                            {({ open }) => (
                                <>
                                    <div>
                                        <Menu.Button className="inline-flex items-center justify-between w-full px-4 py-2 text-sm font-medium text-olm-gray rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 align-middle">
                                            <GroupIcon
                                                className="mr-3 h-6 w-6 text-gray-500 self-center"
                                                aria-hidden="true"
                                            />
                                            <span className="self-center">
                                                {item}
                                            </span>
                                            <ChevronDownIcon
                                                className={`${
                                                    open
                                                        ? 'transform rotate-180'
                                                        : ''
                                                } w-5 h-5 text-gray-500 self-center`}
                                            />
                                        </Menu.Button>
                                    </div>
                                    <Transition
                                        show={open}
                                        enter="transition ease-out duration-100"
                                        enterFrom="transform opacity-0 scale-95"
                                        enterTo="transform opacity-100 scale-100"
                                        leave="transition ease-in duration-75"
                                        leaveFrom="transform opacity-100 scale-100"
                                        leaveTo="transform opacity-0 scale-95">
                                        {(ref) => (
                                            <Menu.Items
                                                ref={ref}
                                                className="origin-top-left absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                                                <div className="py-1">
                                                    {navigation[item].items.map(
                                                        (item) => (
                                                            <Menu.Item
                                                                key={item.name}>
                                                                {({
                                                                    active,
                                                                }) => (
                                                                    <Link
                                                                        href={
                                                                            item.href
                                                                        }>
                                                                        <a
                                                                            className={`${
                                                                                active
                                                                                    ? 'bg-gray-100 text-gray-900'
                                                                                    : 'text-olm-gray hover:bg-gray-50 hover:text-black'
                                                                            } group flex font-medium items-center px-2 py-2 text-sm`}>
                                                                            <item.icon
                                                                                className={classNames(
                                                                                    item.href ===
                                                                                        router.pathname
                                                                                        ? 'text-olh'
                                                                                        : 'text-olm-gray group-hover:text-gray-500',
                                                                                    'mr-3 flex-shrink-0 h-6 w-6',
                                                                                )}
                                                                                aria-hidden="true"
                                                                            />
                                                                            <span className="flex-1">
                                                                                {
                                                                                    item.name
                                                                                }
                                                                            </span>
                                                                        </a>
                                                                    </Link>
                                                                )}
                                                            </Menu.Item>
                                                        ),
                                                    )}
                                                </div>
                                            </Menu.Items>
                                        )}
                                    </Transition>
                                </>
                            )}
                        </Menu>
                    )
                } else {
                    const ItemIcon = navigation[item].icon
                    return (
                        <Link key={item} href={navigation[item].href}>
                            <a
                                className={
                                    'text-black group flex font-medium items-center px-2 py-2 text-sm'
                                }>
                                <ItemIcon
                                    className={classNames(
                                        navigation[item].href ===
                                            router.pathname
                                            ? 'text-olh'
                                            : 'text-black group-hover:text-gray-500',
                                        'mr-3 flex-shrink-0 h-6 w-6',
                                    )}
                                    aria-hidden="true"
                                />
                                <span>{item}</span>
                            </a>
                        </Link>
                    )
                }
            })}
            {!impersonated && (
                <button
                    onClick={handleLogout}
                    className="text-black hover:text-black hover:bg-gray-50 group flex items-center px-2 py-2 text-sm font-medium rounded-md">
                    <LogoutIcon
                        className="text-black group-hover:text-black mr-3 flex-shrink-0 h-6 w-6"
                        aria-hidden="true"
                    />
                    <span className="flex-1">Logout</span>
                </button>
            )}
        </nav>
    )
}

export default AccountNav
